import { useState } from 'react'
import { AlertTriangle, Plus, ShieldAlert } from 'lucide-react'
import {
  DateValue, EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge, EmployeePicker } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import {
  useComplianceMutations, useComplianceSettings, useDsrActions, useDsrRequests, useErasurePlan,
} from '../api'
import type { DsrRequestView } from '../types'

const STATUS_TONE: Record<string, 'neutral' | 'info' | 'success' | 'danger' | 'warning'> = {
  RECEIVED: 'neutral',
  PENDING_APPROVAL: 'info',
  APPROVED: 'warning',
  REJECTED: 'danger',
  COMPLETED: 'success',
  CANCELLED: 'neutral',
}

/**
 * Data-subject requests, and the erasure that must not be careless.
 *
 * <p>The screen shows what an erasure would overwrite <b>before</b> anybody agrees to it, and
 * afterwards what it did — table by table, columns only, never the values. It also says, in
 * as many words, that employment records stay: an erasure here anonymises, and a payslip from
 * three years ago is the employer's record and carries its own retention period.
 */
export function DataRequestsPage() {
  useBreadcrumb()
  const requests = useDsrRequests()
  const settings = useComplianceSettings()
  const plan = useErasurePlan()
  const actions = useComplianceMutations()

  const [selected, setSelected] = useState<DsrRequestView | null>(null)
  const actionLog = useDsrActions(selected?.id)
  const [raising, setRaising] = useState(false)
  const [draft, setDraft] = useState({ employeeId: '', requestType: 'ACCESS', via: '', note: '' })
  const [error, setError] = useState<string | null>(null)
  const [banner, setBanner] = useState<string | null>(null)

  function run(body: () => Promise<unknown>, fallback: string) {
    void (async () => {
      setBanner(null)
      try {
        await body()
      } catch (caught) {
        setBanner(toApiProblem(caught)?.detail ?? fallback)
      }
    })()
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Data-subject requests"
        subtitle="What is held about somebody, and what happens when they ask for it to go."
        actions={
          <Button type="button" onClick={() => { setError(null); setRaising(true) }}>
            <Plus className="size-4" aria-hidden="true" />
            Record a request
          </Button>
        }
      />

      {settings.data?.responseWindowNote && (
        <p className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          {settings.data.responseWindowNote} Set one in compliance settings if your jurisdiction
          imposes a deadline.
        </p>
      )}

      {banner && (
        <p role="alert" className="rounded-md border border-[var(--color-danger)] bg-[var(--color-tint-red)] p-3 text-sm text-[var(--color-danger)]">
          {banner}
        </p>
      )}

      <Card>
        {requests.isPending && <Skeleton className="m-4 h-24" />}
        {requests.isError && (
          <ErrorState error={requests.error} onRetry={() => void requests.refetch()} />
        )}
        {requests.isSuccess && requests.data.length === 0 && (
          <EmptyState
            title="No request has been recorded"
            description="Somebody asking what is held about them, or asking for it to go, is recorded here — including the ones that arrive by email."
          />
        )}
        {requests.isSuccess && requests.data.length > 0 && (
          <table className="w-full text-sm">
            <thead className="border-b border-[var(--color-border)] text-[var(--color-muted)]">
              <tr>
                <th className="p-3 text-start font-medium">Reference</th>
                <th className="p-3 text-start font-medium">Subject</th>
                <th className="p-3 text-start font-medium">Asking for</th>
                <th className="p-3 text-start font-medium">Received</th>
                <th className="p-3 text-start font-medium">Due</th>
                <th className="p-3 text-start font-medium">Status</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {requests.data.map((row) => (
                <tr key={row.id} className="border-b border-[var(--color-border)]">
                  <td className="p-3 font-mono">{row.reference}</td>
                  <td className="p-3">{row.subjectName ?? '—'}</td>
                  <td className="p-3">{row.requestType.toLowerCase()}</td>
                  <td className="p-3"><DateValue value={row.receivedAt} /></td>
                  <td className="p-3">
                    {row.dueAt
                      ? <DateValue value={row.dueAt} />
                      : (
                        <span className="text-[var(--color-muted)]" title={row.dueNote ?? ''}>
                          No window set
                        </span>
                      )}
                  </td>
                  <td className="p-3">
                    <StatusBadge
                      status={row.status}
                      tone={STATUS_TONE[row.status] ?? 'neutral'}
                      label={row.status.toLowerCase().replace('_', ' ')}
                    />
                  </td>
                  <td className="p-3 text-end">
                    <div className="flex flex-wrap justify-end gap-2">
                      <Button
                        type="button" variant="outline" size="sm"
                        onClick={() => setSelected(row)}
                      >
                        Open
                      </Button>
                      {row.requestType === 'ERASURE' && row.status === 'RECEIVED' && (
                        <Button
                          type="button" variant="outline" size="sm"
                          onClick={() => run(
                            () => actions.submitRequest.mutateAsync(row.id),
                            'That request could not be sent for a decision')}
                        >
                          Send for decision
                        </Button>
                      )}
                      {row.requestType === 'ERASURE' && row.status === 'APPROVED' && (
                        <Button
                          type="button" size="sm"
                          onClick={() => run(
                            () => actions.eraseRequest.mutateAsync(row.id),
                            'That erasure could not be run')}
                        >
                          Run erasure
                        </Button>
                      )}
                      {row.requestType !== 'ERASURE'
                        && row.status !== 'COMPLETED' && row.status !== 'CANCELLED' && (
                        <Button
                          type="button" variant="outline" size="sm"
                          onClick={() => run(
                            () => actions.completeRequest.mutateAsync({ id: row.id }),
                            'That request could not be closed')}
                        >
                          Mark answered
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card className="p-4">
        <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-[var(--color-heading)]">
          <ShieldAlert className="size-4" aria-hidden="true" />
          What an erasure does
        </h3>
        <p className="mb-3 text-sm text-[var(--color-body)]">
          It <strong>overwrites and never deletes</strong>. Payroll runs, attendance days and
          assignments stay exactly where they are — they are the employer's records and carry
          retention periods of their own — and the person they point at stops being
          identifiable from this system. Every column touched is written to the request's log,
          which records column names and never their contents.
        </p>
        {plan.isPending && <Skeleton className="h-16" />}
        {plan.data && (
          <ul className="space-y-2 text-sm">
            {plan.data.map((entry) => (
              <li key={entry.table} className="rounded-md border border-[var(--color-border)] p-2">
                <p className="font-mono text-xs text-[var(--color-heading)]">{entry.table}</p>
                <p className="text-xs text-[var(--color-muted)]">{entry.fields.join(', ')}</p>
                <p className="mt-1 text-xs text-[var(--color-body)]">{entry.note}</p>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <FormDialog
        open={selected != null}
        onOpenChange={(open) => { if (!open) setSelected(null) }}
        title={selected ? `Request ${selected.reference}` : ''}
        submitLabel="Close"
        onSubmit={() => setSelected(null)}
      >
        {selected && (
          <div className="space-y-3 text-sm">
            <p>
              <strong>{selected.subjectName ?? 'Unknown person'}</strong> asked for{' '}
              {selected.requestType.toLowerCase()}
              {selected.receivedVia ? ` by ${selected.receivedVia}` : ''}.
            </p>
            {selected.requestedNote && (
              <p className="text-[var(--color-body)]">{selected.requestedNote}</p>
            )}
            {selected.decisionNote && (
              <p className="rounded-md border border-[var(--color-border)] p-2">
                <span className="text-xs text-[var(--color-muted)]">Decision</span><br />
                {selected.decisionNote}
              </p>
            )}
            <div>
              <p className="mb-1 text-xs text-[var(--color-muted)]">What was done</p>
              {actionLog.isPending && <Skeleton className="h-12" />}
              {actionLog.data?.length === 0 && (
                <p className="text-[var(--color-muted)]">Nothing has been erased for this request.</p>
              )}
              {actionLog.data && actionLog.data.length > 0 && (
                <ul className="space-y-1">
                  {actionLog.data.map((entry) => (
                    <li key={entry.id} className="rounded border border-[var(--color-border)] p-2 text-xs">
                      <span className="font-mono">{entry.targetTable}</span>{' '}
                      <span className="text-[var(--color-muted)]">
                        {entry.action.toLowerCase()}
                      </span>
                      {entry.fields.length > 0 && <> · {entry.fields.join(', ')}</>}
                      {entry.retainedReason && (
                        <p className="mt-1 text-[var(--color-body)]">{entry.retainedReason}</p>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </FormDialog>

      <FormDialog
        open={raising}
        onOpenChange={(open) => { if (!open) { setRaising(false); setError(null) } }}
        title="Record a data-subject request"
        description="The clock, if a response window is configured, starts here."
        submitLabel="Record it"
        loading={actions.raiseRequest.isPending}
        error={error}
        onSubmit={() => {
          void (async () => {
            setError(null)
            try {
              await actions.raiseRequest.mutateAsync({
                // The server resolves the person from the employment: a client that had to
                // know the person id is a client that could name the wrong subject.
                subjectPersonId: null,
                subjectEmployeeId: draft.employeeId || null,
                requestType: draft.requestType,
                receivedVia: draft.via || null,
                receivedAt: null,
                requestedNote: draft.note || null,
              })
              setRaising(false)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That request could not be recorded')
            }
          })()
        }}
      >
        <div className="space-y-3">
          <div>
            <Label htmlFor="dsr-employee">Who it is about</Label>
            <EmployeePicker
                  id="dsr-employee"
                  value={draft.employeeId}
                  onChange={(employeeId) => setDraft({ ...draft, employeeId: employeeId ?? '' })}
                />
          </div>
          <div>
            <Label htmlFor="dsr-type">What they asked for</Label>
            <Select
              id="dsr-type" className="w-full" value={draft.requestType}
              onChange={(event) => setDraft({ ...draft, requestType: event.target.value })}
            >
              <option value="ACCESS">Access — what do you hold about me</option>
              <option value="EXPORT">Export — give me a copy</option>
              <option value="RECTIFICATION">Rectification — this is wrong</option>
              <option value="ERASURE">Erasure — remove it</option>
            </Select>
            {draft.requestType === 'ERASURE' && (
              <p className="mt-1 text-xs text-amber-800">
                An erasure needs a decision from somebody other than whoever records it, and is
                refused while the person is still employed unless that has been turned on
                deliberately.
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="dsr-via">How it arrived</Label>
            <Input
              id="dsr-via" value={draft.via} placeholder="Email, letter, in person"
              onChange={(event) => setDraft({ ...draft, via: event.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="dsr-note">What they said</Label>
            <Textarea
              id="dsr-note" value={draft.note}
              onChange={(event) => setDraft({ ...draft, note: event.target.value })}
            />
          </div>
        </div>
      </FormDialog>
    </div>
  )
}
